<!-- Vue registration component -->
<template>
  <!-- Registration Modal -->
  <div
    class="modal modal-search-dish fade"
    id="registerModal"
    tabindex="-1"
    aria-labelledby="registerModalLabel"
    aria-hidden="true"
    ref="registerModal"
  >
    <div class="modal-dialog modal-dialog-scrollable modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <div class="login-content">
            <div class="login-text-wrap">
              <h4>{{ t("sign_up", ["signup"]) || "Sign Up" }}</h4>
              <p>{{ t("signup_subtitle", ["signup"]) || "Hi 👋 there! Sign up to make today\u2019s meals memorable" }}</p>
            </div>

            <!-- Registration Form -->
            <form @submit.prevent="handleRegister">
              <div class="form-wrap">

                <!-- General Error Message -->
                <div v-if="errors.general" class="alert" :class="notice.kind === 'info' ? 'alert-info' : 'alert-danger'" role="alert">
                  {{ errors.general }}
                  <div v-if="notice.actions" class="notice-acts">
                    <a href="#" class="btn btn-sm btn-primary" data-bs-toggle="modal" data-bs-target="#loginModal" @click.prevent>{{ L.signInInstead }}</a>
                    <a href="#" class="btn btn-sm btn-outline-secondary" data-bs-toggle="modal" data-bs-target="#forgotPasswordOTPModal" @click.prevent>{{ L.resetInstead }}</a>
                  </div>
                </div>

                <!-- Full Name Field -->
                <div class="mb-3 form-wrap-content">
                  <label for="fullName" class="form-label">{{ t("full_name", ["signup"]) || "Full Name" }}</label>
                  <div class="position-relative">
                    <div class="icon-email">
                      <i class="fa-solid fa-user"></i>
                    </div>
                    <input
                      type="text"
                      id="fullName"
                      v-model="form.full_name"
                      class="form-control"
                      :placeholder="t('enter_full_name', ['signup']) || 'Enter Full Name'"
                      required
                    />
                    <div class="info-icon" v-if="errors.full_name">
                      <i class="fa-solid fa-circle-exclamation"></i>
                    </div>
                  </div>
                  <div class="error-wrap" v-if="errors.full_name">
                    <p>{{ errors.full_name }}</p>
                  </div>
                </div>

                <!-- Email Address Field -->
                <div class="mb-3 form-wrap-content">
                  <label for="email" class="form-label">{{ t("email_address", ["signup"]) || "Email Address" }}</label>
                  <div class="position-relative">
                    <div class="icon-email">
                      <i class="fa-solid fa-envelope"></i>
                    </div>
                    <input
                      type="email"
                      id="email"
                      v-model="form.email"
                      class="form-control"
                      :placeholder="t('enter_email_address', ['signup']) || 'Enter Email Address'"
                      required
                    />
                    <div class="info-icon" v-if="errors.email">
                      <i class="fa-solid fa-circle-exclamation"></i>
                    </div>
                  </div>
                  <div class="error-wrap" v-if="errors.email">
                    <p>{{ errors.email }}</p>
                  </div>
                </div>

                <!-- Phone Number Field -->
                <div class="mb-3 form-wrap-content">
                  <label for="phoneNumber" class="form-label">{{ t("phone_number", ["signup"]) || "Phone Number" }}</label>
                  <div class="position-relative">
                    <div class="icon-email">
                      <i class="fa-solid fa-phone"></i>
                    </div>
                    <input
                      type="tel"
                      id="phoneNumber"
                      v-model="form.mobile_number"
                      autocomplete="tel"
                      inputmode="tel"
                      class="form-control"
                      :placeholder="t('enter_phone_number', ['signup']) || 'Enter Phone Number'"
                      required
                    />
                    <div class="info-icon" v-if="errors.mobile_number">
                      <i class="fa-solid fa-circle-exclamation"></i>
                    </div>
                  </div>
                  <div class="error-wrap" v-if="errors.mobile_number">
                    <p>{{ errors.mobile_number }}</p>
                  </div>
                </div>

                <!-- Password Field -->
                <div class="mb-3 form-wrap-content">
                  <label for="password" class="form-label">{{ t("password", ["signup"]) || "Password" }}</label>
                  <div class="position-relative">
                    <div class="icon-email">
                      <i class="fa-solid fa-lock"></i>
                    </div>
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      id="password"
                      v-model="form.password"
                      class="form-control"
                      :placeholder="t('enter_password', ['signup']) || 'Enter Password'"
                      required
                    />
                    <button
                      type="button"
                      class="info-icon btn btn-link"
                      @click="togglePasswordVisibility"
                      aria-label="Toggle Password Visibility"
                    >
                      <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                    </button>
                  </div>
                  <div class="error-wrap" v-if="errors.password">
                    <p>{{ errors.password }}</p>
                  </div>
                  <p v-else class="pwhint">{{ L.pwHint }}</p>
                </div>

                <!-- Confirm Password Field -->
                <div class="mb-3 form-wrap-content">
                  <label for="confirmPassword" class="form-label">{{ t("confirm_password", ["signup"]) || "Confirm Password" }}</label>
                  <div class="position-relative">
                    <div class="icon-email">
                      <i class="fa-solid fa-lock"></i>
                    </div>
                    <input
                      :type="showConfirmPassword ? 'text' : 'password'"
                      id="confirmPassword"
                      v-model="form.confirm_password"
                      class="form-control"
                      :placeholder="t('confirm_password', ['signup']) || 'Confirm Password'"
                      required
                    />
                    <button
                      type="button"
                      class="info-icon btn btn-link"
                      @click="toggleConfirmPasswordVisibility"
                      aria-label="Toggle Confirm Password Visibility"
                    >
                      <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                    </button>
                  </div>
                  <div class="error-wrap" v-if="errors.confirm_password">
                    <p>{{ errors.confirm_password }}</p>
                  </div>
                </div>

                <!-- Remember Me & Forgot Password -->
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="rememberMe"
                      v-model="form.remember_me"
                    />
                    <label class="form-check-label" for="rememberMe">
                      {{ t("remember_me", ["signup"]) || "Remember me" }}
                    </label>
                  </div>
                  <div class="forgot-password">
                    <a href="#" data-bs-toggle="modal" data-bs-target="#forgotPasswordOTPModal" @click.prevent>{{ t("forgot_password", ["signup"]) || L.resetInstead }}</a>
                  </div>
                </div>

                <!-- Submit Button -->
                <div class="button-wrap mb-3">
                  <button class="btn btn-primary w-100" type="submit" :disabled="isSubmitting">
                    <span
                      v-if="isSubmitting"
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {{ t("sign_up", ["signup"]) || "Sign Up" }}
                  </button>
                </div>
              </div>

              <!-- Hidden Close Button -->
              <button
                type="button"
                ref="hiddenCloseButton"
                class="d-none"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>

              <!-- Bottom Text -->
              <div class="bottom-text">
                <p>
                  {{ t("already_have_account", ["signup"]) || "Already have an account?" }}
                  <a href="#" data-bs-toggle="modal" data-bs-target="#loginModal" @click.prevent="switchToLogin">{{ t("login_now", ["signup"]) || "Login Now!" }}</a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { authText, passwordMessage, normaliseAuPhone, openCodeStep } from '@/i18n/authText';
import { trackCompleteRegistration } from '@/analytics/track';
import { useAuthStore } from '@/stores/auth';
import { useTranslationStore } from '@/stores/translation';

export default {
  name: 'SignUp',
  setup() {
    const authStore = useAuthStore();
    const translationStore = useTranslationStore();
    const t = (label, modules) => translationStore.translate(label, modules);
    const L = computed(() => authText(translationStore.currentLanguage));
    // how the general message is shown: 'error' (red) or 'info' (blue), and whether to offer
    // "Sign in" / "Forgot password?" buttons (an account already exists)
    const notice = reactive({ kind: 'error', actions: false });
    const registerModal = ref(null);

    const form = reactive({
      full_name: '',
      email: '',
      mobile_number: '',
      password: '',
      confirm_password: '',
      remember_me: false,
    });

    const errors = reactive({
      general: '',
      full_name: '',
      email: '',
      mobile_number: '',
      password: '',
      confirm_password: '',
    });

    const isSubmitting = ref(false);

    const showPassword = ref(false);
    const showConfirmPassword = ref(false);

    const resetForm = () => {
      form.full_name = '';
      form.email = '';
      form.mobile_number = '';
      form.password = '';
      form.confirm_password = '';
      form.remember_me = false;
      Object.keys(errors).forEach((key) => (errors[key] = ''));
    };

    const togglePasswordVisibility = () => {
      showPassword.value = !showPassword.value;
    };

    const toggleConfirmPasswordVisibility = () => {
      showConfirmPassword.value = !showConfirmPassword.value;
    };

    const validateForm = () => {
      let isValid = true;

      // Reset errors
      Object.keys(errors).forEach((key) => (errors[key] = ''));

      notice.kind = 'error'; notice.actions = false;
      const W = L.value;

      if (!form.full_name.trim()) {
        errors.full_name = W.nameReq;
        isValid = false;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      form.email = (form.email || '').trim();
      if (!form.email) {
        errors.email = W.emailReq;
        isValid = false;
      } else if (!emailPattern.test(form.email)) {
        errors.email = W.emailBad;
        isValid = false;
      }

      // '+61 412 345 678', '61412345678', '412 345 678' and '0412 345 678' are all the same number
      const phone = normaliseAuPhone(form.mobile_number);
      if (!phone) {
        errors.mobile_number = W.phoneReq;
        isValid = false;
      } else if (!/^0\d{9}$/.test(phone)) {
        errors.mobile_number = W.phoneBad;
        isValid = false;
      } else {
        form.mobile_number = phone;
      }

      // the same rules the server applies, said up front and in the customer's language
      if (!form.password) {
        errors.password = W.pwReq;
        isValid = false;
      } else if (form.password.length < 8) {
        errors.password = W.pwShort;
        isValid = false;
      } else if (/^\d+$/.test(form.password)) {
        errors.password = W.pwNumeric;
        isValid = false;
      }

      if (!form.confirm_password) {
        errors.confirm_password = W.pwConfirmReq;
        isValid = false;
      } else if (form.password !== form.confirm_password) {
        errors.confirm_password = W.pwMismatch;
        isValid = false;
      }

      return isValid;
    };

    const hiddenCloseButton = ref(null);

    const handleRegister = async () => {
      if (!validateForm()) {
        return;
      }

      isSubmitting.value = true;
      errors.general = '';

      try {
        await authStore.register(form);
        try { trackCompleteRegistration({ meta: { method: 'email' } }); } catch (e) { /* noop */ }
        hiddenCloseButton.value.click();
      } catch (apiErrors) {
        const W = L.value;
        const code = apiErrors && apiErrors.__code;
        const body = (apiErrors && apiErrors.__body) || {};
        if (code === 'claim_code_sent') {
          // they already have an account from a phone order / guest checkout: a code went to
          // that account's own inbox — go straight to "enter code + choose a password"
          if (body.send_status === 'failed') { errors.general = W.codeFailed; return; }
          if (body.send_status === 'limited') { errors.general = W.codeLimited; return; }
          const identifier = body.field === 'mobile_number' ? form.mobile_number : form.email;
          const m = body.masked_email || form.email;
          openCodeStep({ identifier, masked: m, intro: body.sent === false ? W.claimWait(m) : W.claimIntro(m), password: form.password });
          return;
        }
        if (code === 'account_exists') { errors.general = W.exists; notice.actions = true; return; }
        if (code === 'needs_staff_link') { errors.general = W.needsLink; notice.kind = 'info'; return; }
        if (code === 'network') { errors.general = W.network; return; }
        let shown = false;
        Object.keys(errors).forEach((key) => {
          if (key !== 'general' && apiErrors && apiErrors[key]) { errors[key] = apiErrors[key]; shown = true; }
        });
        const pw = passwordMessage(W, body.password_codes);
        if (pw) { errors.password = pw; shown = true; }
        if (!shown) errors.general = W.unexpected;
      } finally {
        isSubmitting.value = false;
      }
    };

    const loginWithFacebook = () => {
      // Implement Facebook login logic
    };

    const loginWithGoogle = () => {
      // Implement Google login logic
    };


    // Function to switch to Login Modal
    const switchToLogin = () => {
      hiddenCloseButton.value.click();
    };

    onMounted(() => {
      if (registerModal.value) {
        registerModal.value.addEventListener('hidden.bs.modal', resetForm);
      }
    });

    onUnmounted(() => {
      if (registerModal.value) {
        registerModal.value.removeEventListener('hidden.bs.modal', resetForm);
      }
    });

    return {
      t,
      L,
      notice,
      form,
      errors,
      isSubmitting,
      showPassword,
      showConfirmPassword,
      togglePasswordVisibility,
      toggleConfirmPasswordVisibility,
      handleRegister,
      loginWithFacebook,
      loginWithGoogle,
      switchToLogin,
      hiddenCloseButton,
      registerModal,
    };
  },
};
</script>


<style scoped>
/* Add your styles here */

.icon-email {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: #6c757d;
}

.info-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #dc3545;
  cursor: pointer;
}

.form-control {
  padding-left: 35px; /* Adjust based on icon size */
  padding-right: 35px; /* Adjust based on icon size */
}

/* New rule to ensure pointer cursor on icons */
.info-icon,
.info-icon i {
  cursor: pointer;
  user-select: none;
}

.button-wrap .spinner-border {
  margin-right: 5px;
}

.btn-link {
  padding: 0;
}

.modal-search-dish .modal-dialog {
  max-width: 500px;
}

.bottom-text {
  margin-top: 10px;
}

.bottom-text a {
  color: #0d6efd;
  text-decoration: none;
}

.bottom-text a:hover {
  text-decoration: underline;
}

/* shubhams style */
.pwhint { margin: 6px 0 0; font-size: 12px; color: #6b7280; text-align: left; }
.notice-acts { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; margin-top: 10px; }
.error-wrap {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.alert {
  margin-bottom: 1rem;
}

.alert-danger {
  color: #842029;
  background-color: #f8d7da;
  border-color: #f5c2c7;
  padding: 0.75rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

.position-relative {
  position: relative;
}

.icon-email {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: #6c757d;
}

.info-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #dc3545;
  cursor: pointer;
}

/* New rule to ensure pointer cursor on icons */
.info-icon,
.info-icon i {
  cursor: pointer;
  user-select: none;
}

.form-control {
  padding-left: 35px;
  padding-right: 35px;
}

.spinner-border {
  margin-right: 0.5rem;
}
</style>
